/* 시공사례 퍼가기 — 편집 화면(다운로드 메뉴)과 "블로그로 퍼가기" 화면이 같이 쓴다.
   · 블로그용 복사: 요약 글 → 공사 개요표 → 페이지마다 글(실제 글자) + 사진 배치 표 → #해시태그. 사진은 템플릿 칸 비율로 잘라 공개 주소로 올린다(blog.ts 참고)
   · 사진 ZIP: 페이지 사진 + 시공 사진 원본을 PNG로 한 파일에 */
import { showcaseApi } from '../api/showcaseApi';
import { blogSummaryPrompt } from '../ai/prompts';
import { allTexts, blogFrames, buildBlog, fallbackSummary, pagePhotoSrcs, type BlogFacts } from './blog';
import { makeZip, photoFrameJpeg, photoToPng, renderPages } from './exporter';
import type { Page } from './types';

export interface ShareTarget { projectId: number; title: string; pages: Page[] }
type Progress = (msg: string) => void;

/** 파일 이름에 못 쓰는 글자 빼기 */
export const fileTitle = (t: string) => (t.trim() || '시공사례').replace(/[\\/:*?"<>|]/g, '_');

/** 개요표·해시태그에 쓸 현장 이름·주소 — 사진이 한 현장 것일 때만(모음집은 현장이 여럿이라 뺀다). 못 받으면 빈 값 */
async function siteFacts(pages: Page[]): Promise<BlogFacts> {
  const ids = [...new Set(pages.flatMap((p) => p.elements.map((e) => e.siteId)).filter((x): x is number => x != null))];
  if (ids.length !== 1) return {};
  try {
    const s = (await showcaseApi.photos(ids[0])).find((x) => x.siteId === ids[0]);
    return s ? { siteName: s.siteName, address: s.address } : {};
  } catch { return {}; }
}

/** 블로그 붙여넣기 내용 만들기 — 사진 칸을 공개 주소로 올리고 HTML·글을 돌려준다 */
export async function buildBlogContent(p: ShareTarget, progress: Progress): Promise<{ html: string; text: string }> {
  const title = p.title.trim();
  // 맨 위 요약 글은 AI가 페이지 글을 읽고 쓴다(사진을 올리는 동안 같이). 안 되면 본문 앞부분으로
  const source = allTexts(p.pages);
  const summaryJob = source
    ? showcaseApi.generate({ projectId: p.projectId, aiType: 'WRITER', temperature: 0.4, prompt: blogSummaryPrompt(title, source) })
      .then((r) => r.text.trim() || fallbackSummary(p.pages)).catch(() => fallbackSummary(p.pages))
    : Promise.resolve('');
  const factsJob = siteFacts(p.pages);
  // 사진은 템플릿 칸 비율로 잘라(표 칸 높이가 맞게) 올린다. 첫 장(page 1번)을 올릴 때 서버가 예전 블로그용 사진을 지운다
  const frames = blogFrames(p.pages), stamp = Date.now().toString(36), photoUrl = new Map<string, string>();
  for (let i = 0; i < frames.length; i++) {
    progress(`블로그용 사진을 올리고 있어요… (${i + 1}/${frames.length})`);
    try { photoUrl.set(frames[i].key, await showcaseApi.blogImage(p.projectId, 'page', i + 1, stamp, await photoFrameJpeg(frames[i].src, frames[i].w, frames[i].h))); }
    catch { /* 한 장이 안 되면 그 칸만 비우고 계속 */ }
  }
  return buildBlog({ pages: p.pages, summary: await summaryJob, facts: await factsJob, photoUrl });
}

/** 클립보드에 쓰기 — 클릭한 그 순간에 불러야 브라우저가 허락한다. 내용은 Promise 로 넘겨 다 만들어지면 채워진다 */
export function writeBlogClipboard(content: Promise<{ html: string; text: string }>): Promise<void> {
  const blob = (type: 'html' | 'text') => content.then((b) => new Blob([b[type]], { type: type === 'html' ? 'text/html' : 'text/plain' }));
  return navigator.clipboard.write([new ClipboardItem({ 'text/html': blob('html'), 'text/plain': blob('text') })])
    .catch(async (err) => {
      const b = await content; // 사진 만들기·올리기 자체가 실패했으면 그 오류를 그대로
      // 브라우저가 Promise 내용을 못 받으면(오래된 브라우저) 다 만든 뒤 한 번 더
      if (err instanceof DOMException) {
        return navigator.clipboard.write([new ClipboardItem({ 'text/html': new Blob([b.html], { type: 'text/html' }), 'text/plain': new Blob([b.text], { type: 'text/plain' }) })]);
      }
      throw err;
    });
}

/** 사진 ZIP — 페이지 사진(PNG 1440×1920) + 시공사진/ 폴더에 원본(PNG) */
export async function buildPhotoZip(p: ShareTarget, progress: Progress): Promise<{ blob: Blob; name: string }> {
  const title = fileTitle(p.title);
  progress(`${p.pages.length}페이지를 사진으로 만들고 있어요…`);
  const pages = await renderPages(p.pages, 'all', { format: 'png', scale: 2 });
  const files = pages.map((f) => ({ name: `${title}_${f.name}페이지.png`, blob: f.blob }));
  const srcs = pagePhotoSrcs(p.pages);
  for (let i = 0; i < srcs.length; i++) {
    progress(`시공 사진을 담고 있어요… (${i + 1}/${srcs.length})`);
    try { files.push({ name: `시공사진/${title}_사진${String(i + 1).padStart(2, '0')}.png`, blob: await photoToPng(srcs[i]) }); }
    catch { /* 한 장이 안 되면 그 사진만 빼고 계속 */ }
  }
  return { blob: await makeZip(files), name: `${title}.zip` };
}

/** 파일 받기 */
export function saveFile(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
